<template>
  <section>
    <p>Cadastre a chave pública do cliente. A chave privada deve permanecer com o cliente, responsável por descriptografar os arquivos.</p>
    <div class="key-toolbar">
      <el-button plain :disabled="!businessClients.length" @click="openForm()">Cadastrar chave</el-button>
      <el-input v-model="search" placeholder="Buscar por nome ou cliente" clearable aria-label="Buscar chaves" />
      <el-button :loading="loading" @click="loadKeys">Atualizar</el-button>
    </div>
    <el-alert v-if="loadError" :title="loadError" type="error" :closable="false" show-icon />
    <el-table v-loading="loading" :data="filteredKeys" empty-text="Nenhuma chave cadastrada">
      <el-table-column prop="name" label="Nome" min-width="160" />
      <el-table-column prop="business_client" label="Cliente" min-width="120" />
      <el-table-column prop="version" label="Versão" width="80" />
      <el-table-column label="Impressão digital (SHA-256)" min-width="200">
        <template #default="{ row }"><span :title="row.fingerprint">{{ row.fingerprint.slice(0, 20) }}…</span></template>
      </el-table-column>
      <el-table-column label="Ações" width="165">
        <template #default="{ row }">
          <el-button link type="primary" @click="openForm(row)">Editar</el-button>
          <el-button link type="danger" :disabled="deleting === row.id" @click="removeKey(row)">Excluir</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-dialog v-model="dialogOpen" :title="editing ? 'Editar chave' : 'Cadastrar chave'" width="min(640px, 95vw)" :close-on-click-modal="false" :before-close="closeDialog">
      <el-form ref="keyForm" :model="form" :rules="rules" label-position="top" :disabled="saving">
        <el-form-item label="Cliente" prop="business_client">
          <el-select v-model="form.business_client" :disabled="!!editing || businessClients.length === 1" placeholder="Selecione o cliente">
            <el-option v-for="client in businessClients" :key="client" :label="client" :value="client" />
          </el-select>
        </el-form-item>
        <el-form-item label="Nome" prop="name"><el-input v-model="form.name" maxlength="120" /></el-form-item>
        <el-form-item label="Descrição"><el-input v-model="form.description" type="textarea" maxlength="1000" /></el-form-item>
        <el-form-item label="Chave pública RSA (PEM)" prop="public_key_pem">
          <el-input v-model="form.public_key_pem" type="textarea" :rows="8" maxlength="8192" placeholder="-----BEGIN PUBLIC KEY-----" spellcheck="false" />
        </el-form-item>
        <p class="key-help">Aceita RSA de 2048, 3072 ou 4096 bits. Não informe a chave privada. Guarde as chaves privadas anteriores para abrir arquivos já enviados.</p>
        <el-alert v-if="editing" title="Salvar cria uma nova versão do cadastro. Arquivos já enviados permanecem associados à versão anterior." type="info" :closable="false" />
        <el-alert v-if="saveError" :title="saveError" type="error" :closable="false" />
      </el-form>
      <template #footer>
        <el-button :disabled="saving" @click="dialogOpen = false">Cancelar</el-button>
        <el-button type="primary" :loading="saving" @click="saveKey">Salvar</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { listEncryptionKeys, createEncryptionKey, updateEncryptionKey, deleteEncryptionKey, encryptionError } from "../../services/encryptionKeys";

export default {
  name: "EncryptionKeysSettings",
  props: { businessClients: { type: Array, default: () => [] } },
  data() {
    return {
      keys: [], search: "", loading: false, loadError: "", dialogOpen: false,
      editing: null, saving: false, saveError: "", deleting: null,
      form: { name: "", description: "", business_client: "", public_key_pem: "" },
      rules: {
        name: [{ required: true, whitespace: true, message: "Informe o nome", trigger: "blur" }],
        business_client: [{ required: true, message: "Selecione o cliente", trigger: "change" }],
        public_key_pem: [{ required: true, whitespace: true, message: "Informe a chave pública PEM", trigger: "blur" }],
      },
    };
  },
  computed: {
    filteredKeys() {
      const term = this.search.trim().toLowerCase();
      return this.keys.filter((key) => this.businessClients.includes(key.business_client)
        && `${key.name} ${key.business_client} ${key.description}`.toLowerCase().includes(term));
    },
  },
  watch: {
    businessClients: {
      deep: true,
      handler() {
        if (!this.saving) this.dialogOpen = false;
      },
    },
  },
  mounted() { this.loadKeys(); },
  methods: {
    async loadKeys() {
      this.loading = true;
      this.loadError = "";
      try { this.keys = await listEncryptionKeys(); }
      catch (error) { this.keys = []; this.loadError = encryptionError(error); }
      finally { this.loading = false; }
    },
    openForm(row) {
      this.editing = row || null;
      this.form = row
        ? { name: row.name, description: row.description, business_client: row.business_client, public_key_pem: row.public_key_pem }
        : { name: "", description: "", business_client: this.businessClients.length === 1 ? this.businessClients[0] : "", public_key_pem: "" };
      this.saveError = "";
      this.dialogOpen = true;
      this.$nextTick(() => this.$refs.keyForm?.clearValidate());
    },
    closeDialog(done) { if (!this.saving) done(); },
    async saveKey() {
      if (this.saving || !(await this.$refs.keyForm.validate().catch(() => false))) return;
      this.saving = true;
      this.saveError = "";
      try {
        if (this.editing) {
          const { name, description, public_key_pem } = this.form;
          await updateEncryptionKey(this.editing.id, { name, description, public_key_pem, version: this.editing.version });
        } else {
          await createEncryptionKey(this.form);
        }
        this.dialogOpen = false;
        ElMessage.success("Chave salva");
        await this.loadKeys();
      } catch (error) { this.saveError = encryptionError(error); }
      finally { this.saving = false; }
    },
    async removeKey(row) {
      try {
        await ElMessageBox.confirm(`Excluir a chave “${row.name}”? Ela não poderá ser selecionada em novos agendamentos. Os arquivos já enviados continuam dependendo da chave privada do cliente.`, "Excluir chave", { type: "warning", confirmButtonText: "Excluir", cancelButtonText: "Cancelar" });
      } catch { return; }
      this.deleting = row.id;
      try {
        await deleteEncryptionKey(row.id);
        ElMessage.success("Chave excluída");
        await this.loadKeys();
      } catch (error) { ElMessage.error(encryptionError(error)); }
      finally { this.deleting = null; }
    },
  },
};
</script>

<style scoped>
.key-toolbar { display: flex; gap: 12px; align-items: center; margin: 20px 0; flex-wrap: wrap; }
.key-toolbar .el-input { width: min(320px, 100%); }
.key-help { color: #526576; font-size: 13px; line-height: 1.5; }
.el-alert { margin: 12px 0; }
.el-select { width: 100%; }
</style>
